'use client';

import { c as _c } from "react/compiler-runtime";
import { type Common, lookup, type Vehicle } from '@cruz-com/lookup';
import { deleteCookie } from 'cookies-next/client';
import dayjs from 'dayjs';
import _ from 'lodash';
import { usePathname } from 'next/navigation';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { myCruzApi } from '@/api';
import { Button, CruzIcon, Divider, Modal, Text } from '@/components';
import { useGlobalStore } from '@/store/provider';
type Props = {
  ignoredFilters?: string[];
  query: Vehicle.Query;
  onFilterChanged: (query: Vehicle.Query) => void;
};
const ignoreCompareKeys = ['page_size', 'page_number', 'sort', 'sort_id', 'date_post', 'location'];

// Memoized utility functions
const formatArray = (val: string) => val.split(',').join(', ');
const getName = (lkp: Common.Option[], val: string | number): string => {
  const x = _.toString(val).split(',');
  const y = lkp.filter(({
    id
  }) => x.includes(`${id}`));
  return y.map(item => item.name).join(', ');
};

// Memoized filter component
const FilterChip = memo(t0 => {
  const $ = _c(21);
  const {
    name,
    label,
    value,
    unit,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== name || $[1] !== onRemove) {
    t1 = () => {
      onRemove(name);
    };
    $[0] = name;
    $[1] = onRemove;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleRemove = t1;
  if (!value) {
    return null;
  }
  const t2 = label || "Show";
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="mb-0.5 text-xs font-light opacity-70">{t2}</div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== unit) {
    t4 = unit && <span className="ml-1 text-xs font-light">{unit}</span>;
    $[5] = unit;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4 || $[8] !== value) {
    t5 = <div className="max-w-[200px] truncate font-medium md:text-sm">{value}{t4}</div>;
    $[7] = t4;
    $[8] = value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <div className="px-3 py-2">{t3}{t5}</div>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `Remove filter: ${label || "Show"} ${value}`;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = twMerge("h-full rounded-r-sm border-l p-1 transition duration-300 hover:cursor-pointer", "hover:text-red-800 dark:hover:text-red-400", "bg-zinc-100 hover:bg-white dark:bg-zinc-900 dark:hover:bg-zinc-700");
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <CruzIcon.Close className="size-3" aria-hidden="true" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== handleRemove || $[16] !== t7) {
    t10 = <button type="button" aria-label={t7} className={t8} onClick={handleRemove}>{t9}</button>;
    $[15] = handleRemove;
    $[16] = t7;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6) {
    t11 = <li className="flex items-center rounded border">{t6}{t10}</li>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
});

// controlled from _searchVehicle
export const VehicleAppliedFilters = memo<Props>(({
  ignoredFilters,
  query,
  onFilterChanged
}) => {
  const baseUrl = usePathname();
  const {
    user,
    setUser,
    modalSignIn,
    setModalSignIn,
    setToast
  } = useGlobalStore(state => state);
  const [showSaveSearch, setShowSaveSearch] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // Memoized check for search saved status
  const isSearchSaved = useMemo(() => {
    if (_.isEmpty(user)) return false;
    const savedSearchList = user?.saved_vehicle_search_list;
    if (_.isEmpty(savedSearchList)) return false;
    const currentQuery = _.omit(query, ignoreCompareKeys);
    const existingSearch = savedSearchList?.find(o => {
      if (o && typeof o === 'object' && 'query' in o) {
        const savedQuery = _.omit((o as {
          query: Vehicle.Query;
        }).query, ignoreCompareKeys);
        return _.isEqual(currentQuery, savedQuery);
      }
      return false;
    });
    return !_.isEmpty(existingSearch);
  }, [query, user]);

  // Memoized remove filter handler
  const removeFilter = useCallback((name: string) => {
    if (name === 'mmt1') {
      deleteCookie('defaultVehicleQuery');
    }
    onFilterChanged({
      ..._.omit(query, name),
      trace: 0
    });
  }, [onFilterChanged, query]);

  // save search
  const handleSaveSearch = useCallback(() => {
    if (_.isEmpty(user)) {
      return setModalSignIn({
        show: true,
        saveSearch: true
      });
    }
    setShowSaveSearch(true);
  }, [user, setModalSignIn]);
  useEffect(() => {
    if (modalSignIn?.saveSearch && !_.isEmpty(user)) {
      setShowSaveSearch(true);
      setModalSignIn({
        show: false
      });
      setToast({
        type: 'success',
        message: 'Search saved successfully.'
      });
    }
  }, [modalSignIn?.saveSearch, user]);
  async function saveSearch() {
    setIsSaving(true);
    setErrMsg(null);
    try {
      const {
        data
      } = await myCruzApi.saveSearch.save({
        name: searchName,
        base_url: baseUrl,
        query: _.omit(query, ['sort_value', 'sort_page_number', 'trace'])
      });
      setShowSaveSearch(false);
      setUser(_.merge(user, data), null);
      setTimeout(() => {
        setSearchName('');
      }, 10);
    } catch (err) {
      setErrMsg(_.get(err, 'message') || 'Sorry, something went wrong. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  }

  // lookup value (memoized)
  const {
    condition,
    bodyType,
    driveType,
    fuelType,
    engineAspiration,
    transmission,
    colorGroup,
    country,
    saleStatus,
    featureTag,
    size,
    upholstery,
    cabStyle
  } = lookup.vehicle;
  const Fc = useCallback(({
    name: name_0,
    label,
    value,
    unit
  }: {
    name: string;
    label?: string;
    value: string | React.ReactNode;
    unit?: string;
  }) => {
    return <FilterChip name={name_0} label={label} value={value} unit={unit} onRemove={removeFilter} />;
  }, [removeFilter]);
  const MMt = ({
    name: name_1,
    mmt
  }: {
    name: string;
    mmt: string | null | undefined;
  }) => {
    if (_.isEmpty(mmt)) {
      return null;
    }
    const x = _.split(mmt, ',');
    const label_0 = ['Make'];
    const make = x[0];
    const model = _.compact(_.split(x[1], '|'));
    if (_.size(model) > 0) {
      label_0.push('Model');
    }
    const trim = _.compact(_.split(x[2], '|'));
    if (_.size(trim) > 0) {
      label_0.push('Trim');
    }
    return <Fc name={name_1} label={label_0.join(', ')} value={<>
            {make}
            {model.length === 1 && <span className='pl-1'>{model[0]}</span>}
            {model.length > 1 && <span className='pl-1'>[{model.join(', ')}]</span>}
            {trim.length === 1 && <span className='pl-1'>{trim[0]}</span>}
            {trim.length > 1 && <span className='pl-1'>[{trim.join(', ')}]</span>}
          </>} />;
  };
  const MM = ({
    name: name_2,
    mmt: mmt_0
  }: {
    name: string;
    mmt: string | null | undefined;
  }) => {
    if (_.isEmpty(mmt_0)) {
      return null;
    }
    const x_0 = _.split(mmt_0, ',');
    const make_0 = x_0[0];
    const model_0 = _.split(x_0[1], '|');
    return <Fc name={name_2} label='Make, Model' value={<>
            {make_0}
            {model_0.length === 1 && <span className='pl-1'>{model_0[0]}</span>}
            {model_0.length > 1 && <span className='pl-1'>[{model_0.join(', ')}]</span>}
          </>} />;
  };
  const renderKeyword = () => {
    if (_.size(query?.keyword) < 4) {
      return null;
    }
    return <Fc name='keyword' label='Keyword' value={query.keyword} />;
  };
  const renderPostAfter = () => {
    if (!query?.date_post) {
      return null;
    }
    return <Fc name='date_post' label='Listed After' value={dayjs(query.date_post).format('MM/DD/YYYY')} />;
  };
  const renderCondition = () => {
    if (!query?.condition || ignoredFilters?.includes('condition')) {
      return null;
    }
    return <Fc name='condition' label='Condition' value={getName(condition, query.condition)} />;
  };
  const renderMinPrice = () => {
    if (!query?.price_min) {
      return null;
    }
    return <Fc name='price_min' label='Minimum Price' value={`$${_.toNumber(query.price_min)?.toLocaleString()}`} />;
  };
  const renderMaxPrice = () => {
    if (!query?.price_max) {
      return null;
    }
    return <Fc name='price_max' label='Maximum Price' value={`$${_.toNumber(query.price_max)?.toLocaleString()}`} />;
  };
  const renderYear = () => {
    if (!query?.year) {
      return null;
    }
    return <Fc name='year' label='Year' value={query.year} />;
  };
  const renderFromYear = () => {
    if (!query?.year_min) {
      return null;
    }
    return <Fc name='year_min' label='From Year' value={query.year_min} />;
  };
  const renderToYear = () => {
    if (!query?.year_max) {
      return null;
    }
    return <Fc name='year_max' label='To Year' value={query.year_max} />;
  };
  const renderMileage = () => {
    if (!query?.mileage) {
      return null;
    }
    return <Fc name='mileage' label='Maximum Mileage' value={_.toNumber(query.mileage)?.toLocaleString()} unit='miles' />;
  };
  const renderGoodDeal = () => {
    if (!query?.good_deal) {
      return null;
    }
    return <Fc name='good_deal' value='Good Deal or Better' />;
  };
  const renderPriceDropped = () => {
    if (!query?.price_dropped) {
      return null;
    }
    return <Fc name='price_dropped' value='Recently Price Dropped' />;
  };
  const renderWithPhoto = () => {
    if (!query?.with_photo) {
      return null;
    }
    return <Fc name='with_photo' value='With Photo(s)' />;
  };
  const renderWithPrice = () => {
    if (!query?.with_price) {
      return null;
    }
    return <Fc name='with_price' value='With Price' />;
  };
  const renderSafest = () => {
    if (!query?.safest) {
      return null;
    }
    return <Fc name='safest' value='5-Star Safety Ratings' />;
  };
  const renderLowMaintenance = () => {
    if (!query?.maintenance) {
      return null;
    }
    return <Fc name='maintenance' value='Low Maintenance' />;
  };
  const renderReliable = () => {
    if (!query?.reliability) {
      return null;
    }
    return <Fc name='reliability' value='Highly Reliable' />;
  };
  const renderComfort = () => {
    if (!query?.comfort) {
      return null;
    }
    return <Fc name='comfort' value='Very Comfortable' />;
  };
  const renderTechnology = () => {
    if (!query?.technology) {
      return null;
    }
    return <Fc name='technology' value='High-Tech' />;
  };
  const renderDynamics = () => {
    if (!query?.dynamics) {
      return null;
    }
    return <Fc name='dynamics' value='Fun to Drive' />;
  };
  const renderBodyType = () => {
    if (!query?.body_type) {
      return null;
    }
    return <Fc name='body_type' label='Type' value={getName(bodyType, query.body_type)} />;
  };
  const renderSaleStatus = () => {
    if (!query?.sale_status) {
      return null;
    }
    return <Fc name='sale_status' label='Sale Status' value={getName(saleStatus, query.sale_status)} />;
  };
  const renderElectricRange = () => {
    if (!query?.electric_range) {
      return null;
    }
    return <Fc name='electric_range' label='Electric Range' value={`${query.electric_range}+`} unit='miles' />;
  };
  const renderDriveType = () => {
    if (!query?.drive_type) {
      return null;
    }
    return <Fc name='drive_type' label='Drive Type' value={getName(driveType as Common.Option[], query.drive_type)} />;
  };
  const renderFuelType = () => {
    if (!query?.fuel_type) {
      return null;
    }
    return <Fc name='fuel_type' label='Fuel Type' value={getName(fuelType as Common.Option[], query.fuel_type)} />;
  };
  const renderFuelEconomy = () => {
    if (!query?.mpg) {
      return null;
    }
    return <Fc name='mpg' label='Fuel Economy' value={`${query.mpg}+`} unit='mpg' />;
  };
  const renderEngineAspiration = () => {
    if (!query?.engine_aspiration) {
      return null;
    }
    return <Fc name='engine_aspiration' label='Engine Aspiration' value={getName(engineAspiration as Common.Option[], query.engine_aspiration)} />;
  };
  const renderEngineCylinder = () => {
    if (!query?.engine_cylinders) {
      return null;
    }
    return <Fc name='engine_cylinders' label='Engine Cylinders' value={formatArray(query?.engine_cylinders?.toString())} />;
  };
  const renderEngineDisplacement = () => {
    if (!query?.engine_displacement) {
      return null;
    }
    return <Fc name='engine_displacement' label='Engine Displacement' value={query.engine_displacement} unit='liters' />;
  };
  const renderTransType = () => {
    if (!query?.trans_type) {
      return null;
    }
    return <Fc name='trans_type' label='Transmission' value={getName(transmission as Common.Option[], query.trans_type)} />;
  };
  const renderTransGear = () => {
    if (!query?.trans_gears) {
      return null;
    }
    return <Fc name='trans_gears' label='Gears' value={formatArray(query?.trans_gears?.toString())} />;
  };
  const renderTowing = () => {
    if (!query?.towing_capacity) {
      return null;
    }
    return <Fc name='towing_capacity' label='Towing Capacity' value={`${_.toNumber(query.towing_capacity)?.toLocaleString()}+`} unit='lbs' />;
  };
  const renderAcceleration = () => {
    if (!query?.acceleration) {
      return null;
    }
    return <Fc name='acceleration' label='Acceleration' value={<div className='flex items-center gap-1'>0-60 mph in {query.acceleration} sec.</div>} />;
  };
  const renderHorsePower = () => {
    if (!query?.horsepower) {
      return null;
    }
    return <Fc name='horsepower' label='Horsepower' value={`${_.toNumber(query.horsepower)?.toLocaleString()}+`} unit='hp' />;
  };
  const renderTorque = () => {
    if (!query?.torque) {
      return null;
    }
    return <Fc name='torque' label='Torque' value={`${query.torque}+`} unit='ft.lbf' />;
  };
  const renderPowerToWeight = () => {
    if (!query?.power_to_weight) {
      return null;
    }
    return <Fc name='power_to_weight' label='Power to Weight Ratio' value={`< ${query.power_to_weight}`} unit='lbs/hp' />;
  };
  const renderExtColor = () => {
    if (!query?.exterior_color) {
      return null;
    }
    return <Fc name='exterior_color' label='Exterior Color' value={getName(colorGroup as Common.Option[], query.exterior_color)} />;
  };
  const renderIntColor = () => {
    if (!query?.interior_color) {
      return null;
    }
    return <Fc name='interior_color' label='Interior Color' value={getName(colorGroup as Common.Option[], query.interior_color)} />;
  };
  const renderUpholstery = () => {
    if (!query?.upholstery) {
      return null;
    }
    return <Fc name='upholstery' label='Upholstery' value={getName(upholstery, query.upholstery)} />;
  };
  const renderMaxSeating = () => {
    if (!query?.max_seating) {
      return null;
    }
    return <Fc name='max_seating' label='Max Seating' value={formatArray(query.max_seating?.toString())} unit='seats' />;
  };
  const renderDoors = () => {
    if (!query?.doors) {
      return null;
    }
    return <Fc name='doors' label='Doors' value={formatArray(query.doors?.toString())} />;
  };
  const renderSize = () => {
    if (!query?.size) {
      return null;
    }
    return <Fc name='size' label='Size' value={getName(size, query.size)} />;
  };
  const renderBedLength = () => {
    if (!query?.bed_length) {
      return null;
    }
    return <Fc name='bed_length' label='Bed Length' value={`${query.bed_length}+`} unit='in.' />;
  };
  const renderCabStyle = () => {
    if (!query?.cab_style) {
      return null;
    }
    return <Fc name='cab_style' label='Cab Style' value={getName(cabStyle as Common.Option[], query.cab_style)} />;
  };
  const renderFeature = () => {
    if (!query?.feature) {
      return null;
    }
    return <Fc name='feature' label='Feature' value={getName(featureTag as Common.Option[], query.feature)} />;
  };
  const renderAssembledIn = () => {
    if (!query?.assembled_in) {
      return null;
    }
    return <Fc name='assembled_in' label='Assembled In' value={getName(country, query.assembled_in)} />;
  };
  const renderSeller = () => {
    if (!query?.seller) {
      return null;
    }
    const x_1 = _.toNumber(query?.seller);
    if (![1, 2].includes(x_1)) {
      return null;
    }
    return <Fc name='seller' label='Seller' value={x_1 === 1 ? 'Dealer' : 'Private'} />;
  };

  // check filters (memoized)
  const hasFilters = useMemo(() => {
    const xFilters = _.chain(query).omit(_.concat(ignoredFilters || [], ['zip_code', 'radius', 'location_id', 'seats_id', 'price_id', 'sort_value', 'sort_page_number', 'trace'])).omitBy(_.isEmpty).value();
    return !_.isEmpty(xFilters);
  }, [query, ignoredFilters]);
  if (!hasFilters) return null;
  return <>
      <Modal open={showSaveSearch} setOpen={setShowSaveSearch}>
        <section aria-label='Save Search Modal'>
          <h2 className='pt-1! text-xl!'>
            Save Search
            <div className='h2-line-red' />
          </h2>
          <div className='w-sm'>
            <Text label='Name this Search' compact={false} name='name' id='name' maxLength={150} autoFocus={true} value={searchName} onChange={e => setSearchName(e.target.value)} />
          </div>
          {errMsg && <div className='form-error-message' role='alert'>
              {errMsg}
            </div>}
          <div className='mt-6 flex justify-end gap-x-3'>
            <Button type='button' variant='outline' onClick={() => setShowSaveSearch(false)} className='h-8 w-28'>
              Cancel
            </Button>
            <Button type='button' className='h-8 w-28' working={isSaving} onClick={saveSearch}>
              Save
            </Button>
          </div>
        </section>
      </Modal>
      <section aria-label='Applied Filters'>
        <ul className='m-0 flex list-none flex-wrap gap-4 p-0 pt-1 pb-2'>
          {renderKeyword()}
          {renderCondition()}
          {renderBodyType()}
          {renderFromYear()}
          {renderToYear()}
          {renderYear()}
          <MMt name='mmt1' mmt={query?.mmt1} />
          <MMt name='mmt2' mmt={query?.mmt2} />
          <MMt name='mmt3' mmt={query?.mmt3} />
          <MM name='mm1' mmt={query?.mm1} />
          <MM name='mm2' mmt={query?.mm2} />
          <MM name='mm3' mmt={query?.mm3} />
          {renderMileage()}
          {renderMinPrice()}
          {renderMaxPrice()}
          {renderWithPhoto()}
          {renderWithPrice()}
          {renderGoodDeal()}
          {renderPriceDropped()}
          {renderSafest()}
          {renderLowMaintenance()}
          {renderReliable()}
          {renderComfort()}
          {renderTechnology()}
          {renderDynamics()}
          {renderSaleStatus()}
          {renderElectricRange()}
          {renderDriveType()}
          {renderFuelType()}
          {renderFuelEconomy()}
          {renderEngineAspiration()}
          {renderEngineCylinder()}
          {renderEngineDisplacement()}
          {renderTransType()}
          {renderTransGear()}
          {renderTowing()}
          {renderAcceleration()}
          {renderHorsePower()}
          {renderTorque()}
          {renderPowerToWeight()}
          {renderExtColor()}
          {renderIntColor()}
          {renderUpholstery()}
          {renderMaxSeating()}
          {renderDoors()}
          {renderCabStyle()}
          {renderBedLength()}
          {renderSize()}
          {renderFeature()}
          {renderAssembledIn()}
          {renderSeller()}
          {renderPostAfter()}
          <li className='place-self-end'>
            <Button type='button' className='h-10 px-4' disabled={isSearchSaved} onClick={handleSaveSearch}>
              {isSearchSaved ? 'Saved' : 'Save Search'}
            </Button>
          </li>
        </ul>
      </section>
      <Divider className='mt-2 mb-6' aria-hidden='true' />
    </>;
});